import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Activity, Play, RotateCcw } from "lucide-react";
import { motion } from "framer-motion";

const stretches = [
  {
    name: "Neck Roll",
    duration: 10,
    instruction: "Gently roll your neck in a circle.",
  },
  {
    name: "Shoulder Shrug",
    duration: 10,
    instruction: "Lift shoulders to ears, then drop.",
  },
  {
    name: "Torso Twist",
    duration: 10,
    instruction: "Twist gently to the left, then right.",
  },
];

const PostureReset = () => {
  const [isActive, setIsActive] = useState(false);
  const [currentStretch, setCurrentStretch] = useState(0);
  const [timeLeft, setTimeLeft] = useState(stretches[0]?.duration || 10);

  useEffect(() => {
    let interval: Timer | null = null;

    if (isActive) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            // Move to next stretch
            if (currentStretch < stretches.length - 1) {
              setCurrentStretch((c) => c + 1);
              return stretches[currentStretch + 1]?.duration || 10;
            } else {
              // Finished
              setIsActive(false);
              setCurrentStretch(0);
              return stretches[0]?.duration || 10;
            }
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, currentStretch]);

  const startSession = () => {
    setCurrentStretch(0);
    setTimeLeft(stretches[0]?.duration || 10);
    setIsActive(true);
  };

  const reset = () => {
    setIsActive(false);
    setCurrentStretch(0);
    setTimeLeft(stretches[0]?.duration || 10);
  };

  const currentStretchData = stretches[currentStretch] ||
    stretches[0] || { name: "", instruction: "", duration: 10 };

  return (
    <Card className="h-full bg-white/40 backdrop-blur-md border-white/20 shadow-sm hover:shadow-md transition-all duration-300">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Activity className="w-5 h-5 text-primary" />
          Posture Reset
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 flex flex-col items-center justify-between h-[200px]">
        {!isActive ? (
          <div className="text-center space-y-4 my-auto">
            <p className="text-muted-foreground">
              Take 30 seconds to reset your posture and relax.
            </p>
            <Button onClick={startSession} className="w-full">
              <Play className="w-4 h-4 mr-2" /> Start 30s Reset
            </Button>
          </div>
        ) : (
          <div className="w-full flex flex-col items-center justify-center space-y-4 h-full">
            <div className="text-center space-y-1">
              <h3 className="text-xl font-bold text-primary">
                {currentStretchData.name}
              </h3>
              <p className="text-sm text-muted-foreground">
                {currentStretchData.instruction}
              </p>
            </div>

            <div className="relative w-20 h-20 flex items-center justify-center">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="transparent"
                  className="text-muted"
                />
                <motion.circle
                  cx="40"
                  cy="40"
                  r="36"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="transparent"
                  className="text-purple-500"
                  strokeDasharray={2 * Math.PI * 36}
                  strokeDashoffset={
                    2 *
                    Math.PI *
                    36 *
                    (1 - timeLeft / currentStretchData.duration)
                  }
                  initial={{ strokeDashoffset: 2 * Math.PI * 36 }}
                  animate={{
                    strokeDashoffset:
                      2 *
                      Math.PI *
                      36 *
                      (1 - timeLeft / currentStretchData.duration),
                  }}
                />
              </svg>
              <span className="absolute text-xl font-bold">{timeLeft}</span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={reset}
              className="text-muted-foreground"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Stop
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PostureReset;
